import { Component, ElementRef } from '@angular/core';
import {
    FormControl,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { BsDatepickerConfig, BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { NgSelectModule } from '@ng-select/ng-select';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { format } from 'date-fns';
import { Subscription } from 'rxjs';
import {
    DateFormats,
    DispositionTypes,
    Events,
    ModalID,
    SirNames,
    StateList,
    Statements,
} from 'src/app/shared/models/constants';
import { DriverLicense } from 'src/app/shared/models/drivers-license-parser';
import { BoundBookEntry, FFL } from 'src/app/shared/models/general';
import { createDateTimeStamp, createEventRecord } from '../../functions';
import { SharedService } from 'src/app/shared/services/shared.service';
import { findStateByAbbreviation } from 'src/app/shared/utils/reference-data.utils';

@Component({
    selector: 'app-edit-bb-disposition',
    templateUrl: './edit-bb-disposition.component.html',
    styleUrls: ['./edit-bb-disposition.component.scss'],
    standalone: true,
    imports: [ReactiveFormsModule, BsDatepickerModule, NgSelectModule],
})
export class EditBbDispositionComponent {
    edit_disposition_form: FormGroup;
    bound_book_entry: BoundBookEntry;
    subscriptions = new Subscription();
    today: Date = new Date();
    dispositionTypes = DispositionTypes;
    selected_dispo_type: DispositionTypes.individual | DispositionTypes.ffl =
        DispositionTypes.individual;
    suffixOptions = SirNames;
    states = StateList;

    // Modals
    modalRef: BsModalRef;
    modalConfig = { animated: true };

    // Datepicker
    bsConfig: Partial<BsDatepickerConfig>;

    private readonly dispositionFields: string[] = [
        'd_ffl',
        'd_disposed_to',
        'd_business_name',
        'd_form4473_id',
        'd_first_name',
        'd_middle_name',
        'd_last_name',
        'd_sr_name',
        'd_address',
        'd_address2',
        'd_zip_code',
        'd_state',
        'd_city',
        'd_date_disposed',
    ];

    constructor(
        protected element: ElementRef,
        private modalService: BsModalService,
        private fb: FormBuilder,
        private sharedService: SharedService,
    ) {
        this.edit_disposition_form = this.fb.group({
            d_ffl: new FormControl({ value: null, disabled: false }),
            d_disposed_to: new FormControl({ value: null, disabled: false }, [
                Validators.required,
            ]),
            d_business_name: new FormControl({ value: null, disabled: false }),
            d_form4473_id: new FormControl({ value: null, disabled: false }),
            d_first_name: new FormControl({ value: null, disabled: false }),
            d_middle_name: new FormControl({ value: null, disabled: false }),
            d_last_name: new FormControl({ value: null, disabled: false }),
            d_sr_name: new FormControl({ value: null, disabled: false }),
            d_address: new FormControl({ value: null, disabled: false }),
            d_address2: new FormControl({ value: null, disabled: false }),
            d_zip_code: new FormControl({ value: null, disabled: false }),
            d_state: new FormControl({ value: null, disabled: false }),
            d_city: new FormControl({ value: null, disabled: false }),
            d_date_disposed: new FormControl({ value: this.today, disabled: false }, [
                Validators.required,
            ]),
            edit_note: new FormControl({ value: null, disabled: false }, [
                Validators.required,
            ]),
        });

        this.bsConfig = Object.assign(
            {},
            {
                containerClass: 'theme-dark-blue',
                dateInputFormat: 'MM/DD/YYYY',
                showWeekNumbers: false,
                isAnimated: true,
                adaptivePosition: true,
            },
        );
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.sharedService.ffl_selected$.subscribe((selectedFfl: FFL) => {
                this.patchFflValues(selectedFfl);
            }),
        );

        this.subscriptions.add(
            this.sharedService.drivers_license_id_selected$.subscribe((driversId: string) => {
                this.patchDriverLicenseValues(driversId);
            }),
        );

        this.selected_dispo_type = this.findDispositionType();

        this.edit_disposition_form.patchValue({
            d_ffl: this.bound_book_entry.d_ffl,
            d_disposed_to: this.bound_book_entry.d_disposed_to,
            d_business_name: this.bound_book_entry.d_business_name,
            d_form4473_id: this.bound_book_entry.d_form4473_id,
            d_first_name: this.bound_book_entry.d_first_name,
            d_middle_name: this.bound_book_entry.d_middle_name,
            d_last_name: this.bound_book_entry.d_last_name,
            d_sr_name: this.bound_book_entry.d_sr_name,
            d_address: this.bound_book_entry.d_address,
            d_address2: this.bound_book_entry.d_address2,
            d_zip_code: this.bound_book_entry.d_zip_code,
            d_state: this.bound_book_entry.d_state,
            d_city: this.bound_book_entry.d_city,
            d_date_disposed: this.bound_book_entry.d_date_disposed,
        });

        this.setDispositionTypeValidators();
    }

    closeModalClick(): void {
        this.edit_disposition_form.reset();
        this.modalService.hide(ModalID.edit_bb_disposition);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    editDispositionClick() {
        if (this.edit_disposition_form.invalid) {
            return;
        }

        if (!this.checkForValuesChanges()) {
            this.sharedService.openMessageModal(Statements.no_values_changed);
            return;
        }

        const valueDifferences = this.returnValueChanges();
        const from_values: any = {};
        const to_values: any = {};

        valueDifferences.forEach((change) => {
            from_values[change.key] = change.from;
            to_values[change.key] = change.to;
        });

        for (const key of this.dispositionFields) {
            (this.bound_book_entry as any)[key] = this.edit_disposition_form.value[key];
        }

        this.bound_book_entry.notes.push({
            note_type: Events.modify_disposition,
            comment: this.edit_disposition_form.value.edit_note,
            username: this.sharedService.logged_in_user.username,
            dateTime: createDateTimeStamp(),
        });

        this.bound_book_entry.event_log.push(
            createEventRecord(
                Events.modify_disposition,
                from_values,
                to_values,
                this.sharedService.logged_in_user.username,
            ),
        );

        this.sharedService.firearm_to_edit$.next(this.bound_book_entry);
        this.closeModalClick();
    }

    checkForValuesChanges() {
        return this.returnValueChanges().length > 0;
    }

    private findDispositionType(): DispositionTypes.individual | DispositionTypes.ffl {
        if (this.bound_book_entry?.d_ffl?.trim()) {
            return DispositionTypes.ffl;
        }

        return DispositionTypes.individual;
    }

    private setControlValidators(controlName: string, validators: ValidatorFn[]): void {
        const control = this.edit_disposition_form.get(controlName);
        if (!control) {
            return;
        }

        control.setValidators(validators);
        control.updateValueAndValidity({ emitEvent: false });
    }

    private setDispositionTypeValidators(): void {
        const isIndividual =
            this.selected_dispo_type === DispositionTypes.individual;

        this.setControlValidators(
            'd_ffl',
            isIndividual ? [] : [Validators.required],
        );
        this.setControlValidators(
            'd_disposed_to',
            isIndividual ? [] : [Validators.required],
        );
        this.setControlValidators(
            'd_form4473_id',
            isIndividual ? [Validators.required] : [],
        );
        this.setControlValidators(
            'd_first_name',
            isIndividual ? [Validators.required] : [],
        );
        this.setControlValidators(
            'd_last_name',
            isIndividual ? [Validators.required] : [],
        );
    }

    changeDispoType(): void {
        if (this.selected_dispo_type === DispositionTypes.individual) {
            this.selected_dispo_type = DispositionTypes.ffl;
        } else {
            this.selected_dispo_type = DispositionTypes.individual;
        }

        this.setDispositionTypeValidators();
    }

    fflSearchClick(): void {
        this.sharedService.openFflSearchModal();
    }

    selectDriversLicenseClick(): void {
        this.sharedService.openIdSelectionModal();
    }

    private patchFflValues(response: FFL): void {
        this.edit_disposition_form.patchValue({
            d_ffl: response.ffl,
            d_disposed_to: response.legal_name,
            d_business_name: response.business_name,
            d_address: response.premise_street,
            d_zip_code: response.premise_zip_code,
            d_city: response.premise_city,
            d_state: findStateByAbbreviation(response.premise_state),
        });
    }

    private patchDriverLicenseValues(driversId: string): void {
        const driverLicense = this.findDriverLicense(driversId);

        if (!driverLicense) {
            return;
        }

        const state = this.states.filter(
            (stateEntry) => stateEntry.state_code === driverLicense.addressState,
        )[0]?.state_name;

        this.edit_disposition_form.patchValue({
            d_first_name: driverLicense.nameFirst,
            d_middle_name: driverLicense.nameMiddle,
            d_last_name: driverLicense.nameLast,
            d_sr_name: null,
            d_address: driverLicense.addressStreet,
            d_address2: null,
            d_zip_code: driverLicense.addressPostalCode,
            d_city: driverLicense.addressCity,
            d_state: state,
        });
    }

    private findDriverLicense(driversId: string): DriverLicense | undefined {
        return this.sharedService.recent_drivers_license.find(
            (license) => license.driversId === driversId,
        );
    }

    private normalizeValue(key: string, value: any): string {
        if (value === null || value === undefined) return '';

        if (key === 'd_date_disposed') {
            const parsed = new Date(value);
            if (Number.isNaN(parsed.getTime())) {
                return String(value).trim();
            }
            return format(parsed, DateFormats.M_D_Y);
        }

        return String(value).trim();
    }

    returnValueChanges(): Array<{ key: string; from: any; to: any }> {
        const changes: Array<{ key: string; from: any; to: any }> = [];

        for (const key of this.dispositionFields) {
            const fromValue = this.normalizeValue(key, (this.bound_book_entry as any)[key]);
            const toValue = this.normalizeValue(key, this.edit_disposition_form.value[key]);

            if (toValue !== fromValue) {
                changes.push({
                    key,
                    from: fromValue,
                    to: toValue,
                });
            }
        }

        return changes;
    }

}
